/**
 * Expedition's contracts.
 *
 * The campaign is a destination: descend 7.9 km, find the heart, and the world
 * is the arc. Expedition is a JOB. The same water, the same boats and the same
 * winch, with the loop rebuilt around going out for something specific and
 * bringing it back — which is the one thing the cargo system was built for and
 * that the campaign only ever asks of you four times.
 *
 * Nothing here is a new mechanic. A contract is a place, a mass and a station,
 * and every part of carrying it out already exists: `spawnLoot` puts the thing
 * in the water, the tow line drags it, `dock` pays for it. What the mode adds is
 * a reason to go somewhere you would not otherwise go, and a payment that makes
 * the trip worth it.
 *
 * THE SITES ARE MEASURED, NOT INVENTED. Each one was found by sweeping the whole
 * world for a point where a crate actually SETTLES in a slot at least 10 m wide,
 * with room above it for the sub to hover and hook on, and at least 90 m from
 * any station so no job is a two-second errand. 44 candidates survived that
 * sweep across fourteen regions; these are the spread of them. A contract that
 * sends you to a crate wedged in a crevice is worse than no contract at all.
 */

import type { PickupType } from './config';
import { Rng } from '../core/noise';
import { STATIONS } from './level';

export interface CargoSite {
  x: number;
  y: number;
  region: string;
  /** Rough descent depth, used to price the job. */
  depth: number;
}

/**
 * Verified drop sites, west to east. Depth is what prices a contract: the west
 * is shallow and safe and pays accordingly, the abyss pays for the trip.
 */
export const SITES: CargoSite[] = [
  { x: -1960, y: -32.8, region: 'SEAGRASS MEADOW', depth: 33 },
  { x: -1880, y: -22.0, region: 'CORAL GARDEN', depth: 22 },
  { x: -1520, y: -7.0, region: 'CORAL GARDEN', depth: 7 },
  { x: -400, y: -12.3, region: 'COASTAL SHELF', depth: 12 },
  { x: -40, y: -52.9, region: 'COASTAL SHELF', depth: 53 },
  { x: 1040, y: -115.1, region: 'MINEFIELD', depth: 115 },
  { x: 1440, y: -146.1, region: 'CAVE SYSTEM', depth: 146 },
  { x: 2020, y: -237.1, region: 'PATROL ZONE', depth: 237 },
  { x: 2400, y: -251.6, region: 'ABYSSAL CHAMBER', depth: 252 },
  { x: 2900, y: -81.4, region: 'KELP FOREST', depth: 81 },
  { x: 3240, y: -116.1, region: 'KELP FOREST', depth: 116 },
  { x: 3700, y: -341.1, region: 'SUNKEN CITY', depth: 341 },
  { x: 4820, y: -658.0, region: 'MILITARY ZONE', depth: 658 },
  { x: 5100, y: -897.4, region: 'CAPITAL BERTH', depth: 897 },
  { x: 5740, y: -1149.0, region: 'THERMAL VENTS', depth: 1149 },
  { x: 6600, y: -1904.2, region: 'THE ABYSS', depth: 1904 },
  { x: 7280, y: -2206.2, region: 'ANCIENT STRUCTURE', depth: 2206 },
];

/** What a job can be for. Flavour only — the physics is the mass. */
const KINDS: { label: string; type: PickupType }[] = [
  { label: 'SURVEY POD', type: 'salvage' },
  { label: 'CORE SAMPLE', type: 'salvage' },
  { label: 'SENSOR DRUM', type: 'salvage' },
  { label: 'BLACK BOX', type: 'salvage' },
  { label: 'REACTOR SLUG', type: 'salvage' },
  { label: 'ANCHOR PLATE', type: 'salvage' },
];

export interface Contract {
  id: string;
  /** What you are fetching. */
  label: string;
  type: PickupType;
  /** Where it is. */
  x: number;
  y: number;
  region: string;
  mass: number;
  /** Face value, before the delivery premium the station already applies. */
  amount: number;
  /** Station id it has to reach. */
  toStation: string;
  toName: string;
  toX: number;
}

/**
 * Build a job.
 *
 * `n` is how many have already been completed, and it is the only difficulty
 * dial: early jobs are shallow, light and short, and the sites open up with
 * depth as the player proves they can get there and back. Deliberately NOT
 * random across the whole world from the first contract — being sent to the
 * abyss on job one is not a challenge, it is a wall.
 */
export function makeContract(n: number, rng: Rng): Contract {
  // The pool of sites grows with experience. Four to start, everything by ten.
  const reach = Math.min(SITES.length, 4 + Math.floor(n * 1.4));
  const pool = [...SITES]
    .sort((a, b) => a.depth - b.depth)
    .slice(0, reach);
  const site = pool[Math.floor(rng.next() * pool.length)];
  const kind = KINDS[Math.floor(rng.next() * KINDS.length)];

  // Heavier as you go, and capped at 1.65 t.
  //
  // The Pathfinder's winch is rated at 1.2 and the Dray's at 5.0, so this tops
  // out just past what the starting boat is comfortable with: the late jobs are
  // a slog on a Pathfinder and nothing on a Dray, which is the right shape for
  // a reason to own the truck. It must not go far beyond that — a contract the
  // boat you own physically cannot finish is a dead end, not a choice, and the
  // first pass reached 2.25 t, which is nearly double the Pathfinder's rating.
  const mass = 0.4 + Math.min(0.9, n * 0.1) + rng.range(0, 0.35);

  // Delivered somewhere ELSE. A job that ends where it starts is a pickup, not
  // an expedition — so the target is the nearest station at least 500 m away,
  // which on this map is always a real haul without ever being absurd.
  const away = STATIONS
    .filter((s) => Math.abs(s.x - site.x) > 500)
    .sort((a, b) => Math.abs(a.x - site.x) - Math.abs(b.x - site.x));
  const dest = away[Math.min(away.length - 1, Math.floor(rng.next() * 2))] ?? STATIONS[0];

  // Paid for the work: the weight, the depth and the distance it has to travel.
  const km = Math.abs(dest.x - site.x) / 1000;
  const amount = Math.round(
    60 + mass * 55 + site.depth * 0.06 + km * 90,
  );

  return {
    id: `c${n}`,
    label: kind.label,
    type: kind.type,
    x: site.x,
    y: site.y,
    region: site.region,
    mass: +mass.toFixed(2),
    amount,
    toStation: dest.id,
    toName: dest.name,
    toX: dest.x,
  };
}
